/**
 * SEO Composable
 *
 * Provides reactive SEO metadata management for Vue 3 applications.
 * Follows Vue 3 best practices with Composition API.
 *
 * Features:
 * - Dynamic meta tag management
 * - Open Graph support
 * - Twitter Card support
 * - JSON-LD structured data
 * - Automatic cleanup on unmount
 *
 * Usage:
 * ```typescript
 * import { useSeo } from '@/composables/useSeo';
 *
 * // In setup()
 * useSeo({
 *   title: 'My Page Title',
 *   description: 'My page description',
 *   openGraph: {
 *     image: '/images/og-image.jpg'
 *   }
 * });
 * ```
 */

import { onMounted, onUnmounted } from 'vue';
import type { SeoMetadata } from '@/types/seo';
import { DEFAULT_SEO, SITE_CONFIG } from '@/types/seo';

/**
 * Track created elements for cleanup
 */
const createdElements = new Set<HTMLElement>();

/**
 * Set document title with site name
 */
function setTitle(title: string): void {
  document.title = `${title} ${SITE_CONFIG.separator} ${SITE_CONFIG.siteName}`;
}

/**
 * Create or update a meta tag
 */
function setMetaTag(
  attribute: 'name' | 'property',
  key: string,
  content: string
): void {
  if (!content) return;

  const selector = `meta[${attribute}="${key}"]`;
  let element = document.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
    createdElements.add(element);
  }

  element.setAttribute('content', content);
}

/**
 * Set canonical URL
 */
function setCanonical(url: string): void {
  if (!url) return;

  const selector = 'link[rel="canonical"]';
  let element = document.querySelector<HTMLLinkElement>(selector);

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
    createdElements.add(element);
  }

  element.setAttribute('href', url);
}

/**
 * Set JSON-LD structured data
 */
function setStructuredData(data: unknown): void {
  if (!data) return;

  // Remove existing structured data script
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript && createdElements.has(existingScript as HTMLElement)) {
    existingScript.remove();
    createdElements.delete(existingScript as HTMLElement);
  }

  // Create new structured data script
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
  createdElements.add(script);
}

/**
 * Set HTML lang attribute
 */
function setLang(lang: string): void {
  if (!lang) return;
  document.documentElement.setAttribute('lang', lang);
}

/**
 * Apply SEO metadata to the document
 * Exported for use in router navigation guards
 */
export function applySeoMetadata(seo: Partial<SeoMetadata>): void {
  // Merge with defaults
  const fullSeo: SeoMetadata = {
    ...DEFAULT_SEO,
    ...seo,
    openGraph: {
      ...DEFAULT_SEO.openGraph,
      ...seo.openGraph,
    },
    twitter: {
      ...DEFAULT_SEO.twitter,
      ...seo.twitter,
    },
  } as SeoMetadata;

  applyMetadata(fullSeo);
}

/**
 * Internal function to apply SEO metadata to the document
 */
function applyMetadata(seo: SeoMetadata): void {
  // Set title
  if (seo.title) {
    setTitle(seo.title);
  }

  // Set basic meta tags
  if (seo.description) {
    setMetaTag('name', 'description', seo.description);
  }

  if (seo.keywords) {
    setMetaTag('name', 'keywords', seo.keywords);
  }

  if (seo.robots) {
    setMetaTag('name', 'robots', seo.robots);
  }

  // Set language
  if (seo.lang) {
    setLang(seo.lang);
  }

  // Set canonical URL
  if (seo.canonical) {
    setCanonical(seo.canonical);
  }

  // Set Open Graph tags
  if (seo.openGraph) {
    const og = seo.openGraph;
    if (og.title) setMetaTag('property', 'og:title', og.title);
    if (og.description) setMetaTag('property', 'og:description', og.description);
    if (og.type) setMetaTag('property', 'og:type', og.type);
    if (og.url) setMetaTag('property', 'og:url', og.url);
    if (og.image) setMetaTag('property', 'og:image', og.image);
    if (og.imageWidth) setMetaTag('property', 'og:image:width', og.imageWidth.toString());
    if (og.imageHeight) setMetaTag('property', 'og:image:height', og.imageHeight.toString());
    if (og.imageAlt) setMetaTag('property', 'og:image:alt', og.imageAlt);
    if (og.locale) setMetaTag('property', 'og:locale', og.locale);
    if (og.siteName) setMetaTag('property', 'og:site_name', og.siteName);
  }

  // Set Twitter Card tags
  if (seo.twitter) {
    const tw = seo.twitter;
    if (tw.card) setMetaTag('name', 'twitter:card', tw.card);
    if (tw.site) setMetaTag('name', 'twitter:site', tw.site);
    if (tw.creator) setMetaTag('name', 'twitter:creator', tw.creator);
    if (tw.title) setMetaTag('name', 'twitter:title', tw.title);
    if (tw.description) setMetaTag('name', 'twitter:description', tw.description);
    if (tw.image) setMetaTag('name', 'twitter:image', tw.image);
    if (tw.imageAlt) setMetaTag('name', 'twitter:image:alt', tw.imageAlt);
  }

  // Set custom meta tags
  if (seo.customMeta) {
    seo.customMeta.forEach((meta) => {
      if (meta.name) {
        setMetaTag('name', meta.name, meta.content);
      } else if (meta.property) {
        setMetaTag('property', meta.property, meta.content);
      }
    });
  }

  // Set structured data
  if (seo.structuredData) {
    setStructuredData(seo.structuredData);
  }
}

/**
 * Cleanup function to remove created elements
 */
function cleanup(): void {
  createdElements.forEach((element) => {
    element.remove();
  });
  createdElements.clear();
}

/**
 * Main composable function
 *
 * @param seoData - SEO metadata configuration
 * @returns Object with update and cleanup functions
 */
export function useSeo(seoData: Partial<SeoMetadata>) {
  // Merge with defaults
  const seo: SeoMetadata = {
    ...DEFAULT_SEO,
    ...seoData,
    openGraph: {
      ...DEFAULT_SEO.openGraph,
      ...seoData.openGraph,
    },
    twitter: {
      ...DEFAULT_SEO.twitter,
      ...seoData.twitter,
    },
  } as SeoMetadata;

  // Apply SEO metadata on mount
  onMounted(() => {
    applyMetadata(seo);
  });

  // Cleanup on unmount
  onUnmounted(() => {
    cleanup();
  });

  /**
   * Update SEO metadata dynamically
   */
  const updateSeo = (newSeoData: Partial<SeoMetadata>): void => {
    const updatedSeo: SeoMetadata = {
      ...seo,
      ...newSeoData,
      openGraph: {
        ...seo.openGraph,
        ...newSeoData.openGraph,
      },
      twitter: {
        ...seo.twitter,
        ...newSeoData.twitter,
      },
    };

    applyMetadata(updatedSeo);
  };

  return {
    updateSeo,
    cleanup,
  };
}

/**
 * Helper function to generate canonical URL
 */
export function getCanonicalUrl(path?: string): string {
  const base = SITE_CONFIG.siteUrl;
  if (!path) {
    return typeof window !== 'undefined' ? window.location.href : base;
  }
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Helper function to generate absolute image URL
 */
export function getAbsoluteImageUrl(imagePath: string): string {
  if (imagePath.startsWith('http')) {
    return imagePath;
  }
  return `${SITE_CONFIG.siteUrl}${imagePath.startsWith('/') ? imagePath : `/${imagePath}`}`;
}
